/* istanbul ignore file */
import { Platform } from 'react-native'

import { env } from '@/src/config/env'

/** Attribute names match the RFC 6265 Set-Cookie casing. */
type TCookieSerializeOptions = {
  Domain?: string
  Expires?: Date
  HttpOnly?: boolean
  'Max-Age'?: number
  Path?: string
  SameSite?: 'Lax' | 'Strict' | 'None'
  Secure?: boolean
}

type TCookieConfig = TCookieSerializeOptions | 'thirtyMinSecure' | 'oneYear' | 'oneMonth' | 'session'

export type TCookieName =
  | 'jwtToken'
  | 'sponsorUik'
  | 'latestUik'
  | 'landingInfo'
  | 'arrivalPrayerPrompt'
  | 'birthdayPrayerPrompt'
  | 'prayerPromptAndBubble'
  | '_ga'
  | 'deviceID'
  | 'isNative'
  | 'sessionInfo'
  | 'gtmData'
  | 'connectDetails'
  // Required to fix a VWO issue on iOS
  | '_vis_opt_exp_60_combi'

const getConfig = (config: TCookieConfig) => ({
  Path: '/',
  Domain: window.location.hostname,
  ...(typeof config === 'string'
    ? {
        // HttpOnly and Secure cannot be set via document.cookie — see
        // https://community.cloudflare.com/t/adding-secure-httponly-to-set-cookie-header/78668
        thirtyMinSecure: {
          Expires: new Date(Date.now() + 30 * 60 * 1000),
          // Prevent CSRF attacks
          SameSite: 'Strict' as const,
        },
        oneYear: { Expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000) },
        oneMonth: { Expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) },
        session: {},
      }[config]
    : config),
})
const getPrefixedName = (name: TCookieName) => `${env.EXPO_PUBLIC_APP_VARIANT === 'prod' ? '____' : 'test_'}${name}`

const serializeCookieAttributes = (attributes: Record<string, unknown>): string =>
  Object.entries(attributes).reduce<string>((acc, [key, value]) => {
    if (value === false || value === undefined) return acc
    if (value === true) return `${acc}; ${key}`
    if (value instanceof Date) return `${acc}; ${key}=${value.toUTCString()}`
    if (typeof value === 'string' || typeof value === 'number') return `${acc}; ${key}=${value}`
    return acc
  }, '')

/** From https://stackoverflow.com/a/48706852 */
export const Cookie = {
  get: <T extends string = string>(name: TCookieName): T | undefined => {
    // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- generic bridge
    if (Platform.OS !== 'web') return '' as T
    const match = new RegExp(`(?:(?:^|.*; *)${getPrefixedName(name)} *= *([^;]*).*$)|^.*$`).exec(document.cookie)
    // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- generic bridge
    return name && match?.[1] ? (decodeURIComponent(match[1]) as T) : undefined
  },
  set: (name: TCookieName, value: string | number | boolean, config: TCookieConfig) => {
    if (Platform.OS === 'web') {
      if (name === '_vis_opt_exp_60_combi') {
        document.cookie = `${name}=${value}${serializeCookieAttributes(getConfig(config))}`
      } else {
        document.cookie = `${getPrefixedName(name)}=${encodeURIComponent(value)}${serializeCookieAttributes(getConfig(config))}`
      }
    }
  },
  delete: (name: TCookieName, config: TCookieConfig) => {
    Cookie.set(name, '', { ...getConfig(config), 'Max-Age': -1 })
  },
  clearAll: () => {
    if (Platform.OS !== 'web') return
    document.cookie.split(';').forEach((cookie) => {
      const [name] = cookie.split('=')
      document.cookie = `${name.trim()}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`
      document.cookie = `${name.trim()}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`
    })
  },
}
